// @flow
import React, { Component, Fragment } from 'react';
import type { Store } from 'redux';
import { connect, Provider } from 'react-redux';
import { IntlProvider } from 'react-intl';
import type { IntlShape } from 'react-intl';
import {
  HashRouter as Router,
  Redirect,
  Route,
  Switch,
} from 'react-router-dom';
import Modal from 'react-modal';

import { bindActionCreatorsSafe } from 'util/safeDispatch';
import type {
  BackupRestoreStatus,
  Dispatch,
  LicensedFeatures,
  State,
} from 'state/types';
import {
  getActivePanelId,
  getBackupRestoreStatus,
  getHasIdentityService,
  getIntl,
  getIsFullAdmin,
  getIsLoggedIn,
  getLicensedFeatures,
} from 'state/selectors';
import Header from './Header';
import LoginModal from './Login/LoginModal';
import AboutModal from './About/AboutModal';
import ModalPrompt from 'view/App/DialogPanels/ModalPrompt';
import Nav from './Nav';
import { mainPages } from './pageData';
import '../admin-styles.css';
import { ABOUT_PANEL_ID } from 'util/constants';
import { actions as dialogActions } from 'portfolio-shared/state/reducers/dialogPanelReducer';
import { actions as authActions } from 'state/reducers/authReducer';
import GlobalNotifications from 'view/App/NotificationDisplay/GlobalNotifications';
import BackupRestoreProgressDialog from './DialogPanels/BackupRestoreProgressDialog';
import { cancelBackup } from 'state/commands/adminPropertiesCommands';

const { showDialogPanel, hideDialogPanel } = dialogActions;
const { updateLastActivity } = authActions;

type Props = {
  activePanelId: ?string,
  backupRestoreStatus: ?BackupRestoreStatus,
  cancelBackup: typeof cancelBackup,
  hasIdentityService: boolean,
  hideDialogPanel: typeof hideDialogPanel,
  intl: IntlShape,
  isFullAdmin: boolean,
  isLoggedIn: boolean,
  licensedFeatures: LicensedFeatures,
  showDialogPanel: typeof showDialogPanel,
  store: Store<Dispatch, State>,
  updateLastActivity: typeof updateLastActivity,
};

class App extends Component<Props> {
  renderLogin: Function;
  showAbout: Function;

  constructor() {
    super();
    this.showAbout = this.showAbout.bind(this);
    this.renderLogin = this.renderLogin.bind(this);
  }

  componentWillMount() {
    // react-modal needs to know the App element  so screen
    // readers don't see main content when a modal is opened.
    Modal.setAppElement(document.getElementById('root'));
  }

  showAbout() {
    this.props.showDialogPanel(ABOUT_PANEL_ID);
  }

  renderLogin() {
    return (
      <LoginModal
        isFullAdmin={this.props.isFullAdmin}
        showAbout={this.showAbout}
      />
    );
  }

  render() {
    const {
      activePanelId,
      backupRestoreStatus,
      cancelBackup,
      hasIdentityService,
      hideDialogPanel,
      intl: { locale, messages },
      isFullAdmin,
      isLoggedIn,
      licensedFeatures: { main },
      updateLastActivity,
    } = this.props;
    return (
      <IntlProvider key={locale} locale={locale} messages={messages}>
        <Provider store={this.props.store}>
          <Router>
            <div
              className="app"
              onClick={updateLastActivity}
              onKeyDown={updateLastActivity}
            >
              <div className="main">
                <ModalPrompt />
                <GlobalNotifications />
                <Header />
                <AboutModal
                  isOpen={activePanelId === ABOUT_PANEL_ID}
                  onClose={hideDialogPanel}
                />

                {isLoggedIn && (
                  <Fragment>
                    <Nav
                      hasIdentityService={hasIdentityService}
                      isFullAdmin={isFullAdmin}
                      isLicensed={main}
                      navItemClass="nav-item"
                      navListClass="nav-list"
                      pages={mainPages}
                    />
                    <BackupRestoreProgressDialog
                      activePanelId={activePanelId}
                      backupRestoreStatus={backupRestoreStatus}
                      cancelBackup={cancelBackup}
                      hideDialogPanel={hideDialogPanel}
                    />
                  </Fragment>
                )}
                <Switch>
                  <Route path="/login" render={this.renderLogin} />
                  {!isLoggedIn && <Redirect to="/login" />}
                  <Redirect
                    exact
                    from="/globalSettings"
                    strict
                    to="/globalSettings/dataLocations"
                  />
                </Switch>
              </div>
            </div>
          </Router>
        </Provider>
      </IntlProvider>
    );
  }
}

App = connect(
  state => ({
    activePanelId: getActivePanelId(state),
    backupRestoreStatus: getBackupRestoreStatus(state),
    hasIdentityService: getHasIdentityService(state),
    intl: getIntl(state),
    isFullAdmin: getIsFullAdmin(state),
    isLoggedIn: getIsLoggedIn(state),
    licensedFeatures: getLicensedFeatures(state),
  }),
  (dispatch: Dispatch) =>
    bindActionCreatorsSafe({
      cancelBackup,
      hideDialogPanel,
      showDialogPanel,
      updateLastActivity,
    })
)(App);

export default App;



// WEBPACK FOOTER //
// ./src/view/App/App.js